import moment from "moment";
import Papa from "papaparse";

import {
  ACCOMMODATION_TYPE,
  ASSESSMENT_CYCLE_PHASE,
  ASSESSMENT_CYCLE_TASK,
  CURRENCY,
  DATE_TYPE,
  QUESTION_SET_DOMAIN,
  QUESTION_SET_STAGE,
  roles,
  STAGE_VALUE,
  STATUS,
  studentStatus,
} from "../constant/constants";
import axios from "axios";
import { toast } from "react-toastify";
import { formRegEx, formRegExReplacer } from "@/constant/regex";
import { learningLanguagesOptions } from "constant/data";

export const apiHeader = (token, isFormData) => {
  const headers = {
    "ngrok-skip-browser-warning": "69420",
    "timezone-offset": moment().utcOffset(),
    "Content-Type": isFormData ? "multipart/form-data" : "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return { headers };
};

// A student sitting a test must never be left staring at a request that will
// never come back. Losing the wifi mid-request kills the connection silently —
// no error reaches the browser — so with no limit the app waits for ever: the
// button stays on "Saving...", a second press is ignored, and the only way out
// is to leave the test, which costs the student a proctoring flag. Twenty
// seconds is far longer than any of these calls take and short enough to
// recover from inside a sitting.
//
// Used only on the student's own test calls. Uploads go straight to S3 outside
// this wrapper, and anything waiting on AI scoring is legitimately slow, so
// neither is given a limit.
export const TEST_REQUEST_TIMEOUT = 20000;

export const testRequestConfig = (token, isFormData) => ({
  ...apiHeader(token, isFormData),
  timeout: TEST_REQUEST_TIMEOUT,
});

// Client-owned schools use the product without paying: no bundle, no credits,
// none of the billing UI. The flag lives on the school rather than the user, so
// one value covers its admin, specialists and students alike.
export const isInternalSchool = (user) => !!user?.school?.isInternal;

// Whether a user may reach the app at all. Only school admins were ever gated
// on payment; internal schools clear the gate without owning a bundle.
export const hasSystemAccess = (user) =>
  user?.role !== roles.SCHOOL_ADMIN ||
  isInternalSchool(user) ||
  user?.school?.bundle?.status === "paid";

export const CreateFormData = (data) => {
  const formData = new FormData();
  for (let key in data) {
    formData.append(key, data[key]);
  }
  return formData;
};

export const formatMessageDate = (date) => {
  const messageDate = moment(date);
  const now = moment();

  if (messageDate.isSame(now, "minute")) {
    // If the date is exactly now, show the current time, e.g., 'HH:mm A'
    return now.format("HH:mm A");
  } else if (messageDate.isSame(now, "day")) {
    // If the date is today, use fromNow()
    return messageDate.fromNow();
  } else if (messageDate.isSame(now.subtract(1, "day"), "day")) {
    // If the date is yesterday, use fromNow()
    return messageDate.fromNow();
  } else {
    // Otherwise, show the date in a standard format, e.g., 'MM/DD/YYYY'
    return messageDate.format("MM/DD/YYYY");
  }
};

export const formatDate = (date, format = "MM/DD/YYYY") => {
  const formdatedDate = moment(date);
  return formdatedDate.format(format);
};

export const formatTime = (time) => {
  const formdatedTime = moment(time);
  return formdatedTime.format("HH:mm A");
};

export function stripAllHtmlTags(input) {
  return input?.replace(/<[^>]*>?/gm, "");
}

const options = {
  onDownloadProgress: (progressEvent) => {
    const { loaded, total } = progressEvent;
  },
};
export function downloadFileFromUrl(fileUrl, filename) {
  axios
    .get(fileUrl, {
      responseType: "blob",
      ...options,
    })
    .then(function (response) {
      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: response.headers["content-type"],
        })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
    })
    .catch((e) => {});
}

export const scrollToTop = (topVal = 0) => {
  window.scrollTo({
    top: topVal,
    behavior: "smooth",
  });
};

export const formatNumber = (num) => {
  if (num % 1 === 0) {
    return `${CURRENCY}${num
      ?.toString()
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, `${CURRENCY}1,`)}`;
  } else {
    return `${CURRENCY}${
      num?.toFixed(2)?.replace(/(\d)(?=(\d{3})+(?!\d))/g, `${CURRENCY}1,`) || 0
    }`;
  }
};

export const quillValidateHandler = (
  params,
  toastMessage = "Please fill the home cms field!"
) => {
  let flag = true;
  for (let key in params) {
    const parsedHTML = new DOMParser().parseFromString(
      params[key],
      "text/html"
    );
    const plainText = parsedHTML.body.textContent || "";
    if (plainText?.trim() === "") {
      toast.error(toastMessage);
      flag = false;
      break;
    }
  }
  return flag;
};

export const camelCaseToLower = (str) => {
  return str.replace(formRegEx, formRegExReplacer).toLowerCase();
};

export const camelCaseToUpper = (str = "") => {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")

    .replace(/_/g, " ")

    .split(/[\s-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("-");
};
export const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const validateParams = (params) => {
  let flag = true;
  for (let key in params) {
    if (!params[key] || params[key]?.length === 0) {
      toast.error(`${key.replace(formRegEx, formRegExReplacer)} is required`);
      flag = false;
      break;
    }
  }
  return flag;
};
export const getStatus = (status) => {
  return status === STATUS.ACTIVE ? "Active" : "Inactive";
};

export const getUserName = (user) => {
  if (!user) return "N/A";
  return (user?.firstName || "") + " " + (user?.lastName || "");
};

export const getYearRange = () => {
  const years = [];
  for (let i = 0; i < 3; i++) {
    years.push({
      label: `${moment().subtract(i, "year").format("YYYY")}`,
      value: `${moment().subtract(i, "year").format("YYYY")}`,
    });
  }
  return years;
};

export const RenderToast = (type = "error", message = "") => {
  let toastId = "error-render-toast";

  if (!type || !message) return;

  toast[type](message, {
    id: toastId,
  });
};
export const isObjectEmpty = (obj) => {
  return Object.keys(obj).length === 0;
};

export const validateNestedParams = (
  params,
  parentKey = "",
  options = { disallowEmptyArray: true, optionalKeys: [] }
) => {
  const { disallowEmptyArray = true, optionalKeys = [] } = options;

  const formatKey = (rawKey) => {
    return rawKey
      .replace(/\[(\d+)\]/g, (_, i) => ` ${parseInt(i) + 1}`) // [0] => 1
      .split(".")
      .map((part) =>
        part.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
      )
      .join(" → ");
  };

  // helper: get the last plain key name (strip [0], etc.)
  const lastKeyName = (keyPath) => {
    if (!keyPath) return "";
    const last = keyPath.split(".").pop() || "";
    return last.replace(/\[\d+\]/g, "");
  };

  // if the current node itself is an optional key path, skip emptiness checks
  const isCurrentOptional = optionalKeys.includes(lastKeyName(parentKey));

  if (Array.isArray(params)) {
    if (disallowEmptyArray && params.length === 0 && !isCurrentOptional) {
      RenderToast("error", `Array cannot be empty at: ${formatKey(parentKey)}`);
      return false;
    }

    for (let i = 0; i < params.length; i++) {
      const key = `${parentKey}[${i}]`;
      if (!validateNestedParams(params[i], key, options)) return false;
    }
  } else if (typeof params === "object" && params !== null) {
    const keys = Object.keys(params);

    // If this object corresponds to an optional key and it's empty, allow it.
    if (keys.length === 0 && parentKey && isCurrentOptional) {
      return true;
    }

    if (keys.length === 0 && parentKey && !isCurrentOptional) {
      RenderToast("error", `Empty object at: ${formatKey(parentKey)}`);
      return false;
    }

    for (const key of keys) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      const keyIsOptional = optionalKeys.includes(key);

      const value = params[key];

      // If this key is optional and its value is "empty", just skip validating it.
      const isEmptyPrimitive =
        value === "" || value === null || value === undefined;

      if (keyIsOptional) {
        // Allow empty primitives
        if (isEmptyPrimitive) continue;

        // Allow empty arrays/objects for optional keys as well
        if (Array.isArray(value) && value.length === 0) continue;
        if (
          typeof value === "object" &&
          value !== null &&
          Object.keys(value).length === 0
        )
          continue;
      }

      if (!validateNestedParams(value, fullKey, options)) return false;
    }
  } else {
    const empty = params === "" || params === null || params === undefined;

    if (empty && !isCurrentOptional) {
      RenderToast("error", `Missing value at: ${formatKey(parentKey)}`);
      return false;
    }
  }

  return true;
};

export const getTaskDomains = (task) => {
  if (task === ASSESSMENT_CYCLE_TASK.SUMMATIVE) {
    return [
      QUESTION_SET_DOMAIN.VOCABULARY,
      QUESTION_SET_DOMAIN.READING,
      QUESTION_SET_DOMAIN.LISTENING,
      QUESTION_SET_DOMAIN.WRITING,
      QUESTION_SET_DOMAIN.SPEAKING,
    ];
  }
  if (task === ASSESSMENT_CYCLE_TASK.READING_WRITING) {
    return [
      QUESTION_SET_DOMAIN.VOCABULARY,
      QUESTION_SET_DOMAIN.READING,
      QUESTION_SET_DOMAIN.WRITING,
    ];
  }
  if (task === ASSESSMENT_CYCLE_TASK.LISTENING_SPEAKING) {
    return [
      QUESTION_SET_DOMAIN.VOCABULARY,
      QUESTION_SET_DOMAIN.LISTENING,
      QUESTION_SET_DOMAIN.SPEAKING,
    ];
  }
  return [];
};

// max flags a student can raise before their test session is ended
export const MAX_TEST_FLAGS = 3;

// Flags are read domain by domain, in the order a report lists its domains: a
// single total would not say which sitting was flagged. Vocabulary is absent
// because no report scores it, so a flag against it would have nowhere to sit.
export const REPORT_FLAG_DOMAINS = [
  { key: "listening", short: "L", label: "Listening" },
  { key: "speaking", short: "S", label: "Speaking" },
  { key: "reading", short: "R", label: "Reading" },
  { key: "writing", short: "W", label: "Writing" },
];

// A domain the student never sat has no sitting to flag, so it is left out
// rather than shown as a clean 0/3. The counts come from the same sitting as
// the scores beside them — the backend reduces both to the latest.
export const getReportFlags = (studentData) =>
  REPORT_FLAG_DOMAINS.filter(
    (domain) => !!studentData?.[`${domain.key}Score`]
  ).map((domain) => ({
    ...domain,
    count: studentData?.[`${domain.key}FlagCount`] ?? 0,
  }));

// One line for the PDFs, which cannot lay out chips the way a page can. Only
// domains carrying a flag appear — a line of 0/3 counts reads as an alarm on a
// report where nothing was raised.
export const getReportFlagText = (studentData) => {
  const flags = getReportFlags(studentData).filter(({ count }) => count > 0);
  if (!flags.length) return "";
  return `Flag Count: ${flags
    .map(({ short, count }) => `${short} ${count}/${MAX_TEST_FLAGS}`)
    .join("   ")}`;
};

// test session status of a single domain, as shown to the conductor
export const getDomainTestStatus = (testSession) => {
  switch (testSession?.status) {
    case "marked":
      return "complete";
    case "completed":
      return "needs-score";
    case "started":
      return "in-progress";
    case "paused":
      return "paused";
    default:
      return null;
  }
};

export const getDateRange = (dates, dateType) => {
  const dateRange =
    dateType === DATE_TYPE.INDIVIDUAL
      ? dates?.map((date) => formatDate(date)).join(", ")
      : `${formatDate(dates?.[0])} to ${formatDate(
          dates?.[dates?.length - 1]
        )}`;

  return dateRange;
};

export const convertSecondsToMinutes = (duration) => {
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  let formatTime = "";

  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(seconds).padStart(2, "0");

  formatTime = `${formattedMinutes} mins`;
  if (seconds > 0) {
    formatTime += ` ${formattedSeconds} secs`;
  }
  return formatTime;
};

export const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  const str = camelCaseToLower(string)
    ?.replaceAll("-", " ")
    ?.replaceAll("_", " ");
  return str?.charAt(0)?.toUpperCase() + str?.slice(1);
};
export const getScore = (score, decimal = 2) => {
  if (score === null || score === undefined) return "-";
  return score?.toFixed(decimal);
};
export const getAverage = (ele, decimal = 2) => {
  const array = Object.values(ele);
  const sum = array.reduce((acc, curr) => acc + curr, 0);
  return getScore(sum / array.length, decimal);
};

export const studentGradeOptions = (function () {
  const options = [];
  for (let i = 3; i < 13; i++) {
    options.push({ label: `Grade ${i}`, value: i });
  }
  return options;
})();

export const secondToMinutes = (seconds) => {
  return seconds / 60;
};

export const minutesToSeconds = (minutes) => {
  return minutes * 60;
};

// generatedAt opens the last tab (Communicate) early: Release lives there, and
// it is what moves the phase on to it, so waiting for the phase would deadlock.
export const getAvailablePhases = (phase, generatedAt) => {
  const phases = [
    ASSESSMENT_CYCLE_PHASE.PREPARE,
    ASSESSMENT_CYCLE_PHASE.CONDUCT,
    ASSESSMENT_CYCLE_PHASE.STANDARDIZE,
    ASSESSMENT_CYCLE_PHASE.EVALUATE,
    ASSESSMENT_CYCLE_PHASE.COMMUNICATE,
    ASSESSMENT_CYCLE_PHASE.INSIGHTS,
  ];

  const phaseIndex = phases.indexOf(phase);

  const available = phases.slice(0, phaseIndex + 1);
  if (generatedAt && phase === ASSESSMENT_CYCLE_PHASE.COMMUNICATE) {
    available.push(ASSESSMENT_CYCLE_PHASE.INSIGHTS);
  }
  return available;
};

export const getReadingAndListeningStage = (score) => {
  const stageValue = Math.max(1, Math.floor(score));
  const stage = Object.keys(STAGE_VALUE).find(
    (key) => STAGE_VALUE[key] === stageValue
  );
  return stage;
};
export const getVocabularyStage = (score) => {
  if (score > 15) return QUESTION_SET_STAGE.TRAILBLAZER;
  if (score > 6) return QUESTION_SET_STAGE.FORERUNNER;
  return QUESTION_SET_STAGE.EXPLORER;
};

export const getNextYears = (range) => {
  const years = [];
  for (let i = 0; i < range; i++) {
    years.push({
      label: `${moment().add(i, "year").format("YYYY")}`,
      value: `${moment().add(i, "year").format("YYYY")}`,
    });
  }
  return years;
};

export const getVariationLevel = (num) => {
  if (num === null || num === undefined || num === 0) return "-";
  else if (num <= 1.5) {
    return "Low";
  } else if (num <= 3) {
    return "Moderate";
  } else if (num <= 4.5) {
    return "High";
  } else {
    return "Very High";
  }
};

// Colour bands used by the criteria heat maps. Single source of truth: the
// cell shading and the key rendered below the heat map both read from here, so
// a threshold can never be changed in one place and missed in the other.
// `textColor` is only set where the band is too dark for the default cell text
// to stay legible; the lighter bands leave it undefined and keep inheriting.
export const variationLevelScale = [
  { max: 1.9, label: "0 - 1.9", color: "#f2f6fb" },
  { max: 2.9, label: "2.0 - 2.9", color: "#d6e7f5" },
  { max: 3.9, label: "3.0 - 3.9", color: "#a2cefa" },
  { max: 4.9, label: "4.0 - 4.9", color: "#6f8f7a" },
  { max: 5.9, label: "5.0 - 5.9", color: "#2f6f73", textColor: "#ffffff" },
  { max: Infinity, label: "6.0 +", color: "#163b6e", textColor: "#ffffff" },
];

const getVariationLevelBand = (num) => {
  if (num === "-" || !num) return null;
  return variationLevelScale.find((band) => num <= band.max);
};

export const getVariationLevelColor = (num) => {
  return getVariationLevelBand(num)?.color || "transparent";
};

export const getVariationLevelTextColor = (num) => {
  return getVariationLevelBand(num)?.textColor;
};
export const getVariationIndexColor = (num) => {
  if (num === "-" || !num) return "transparent";
  if (num <= 1.5) {
    return "#A3CEFA";
  } else if (num <= 3) {
    return "#4B7FA8";
  } else if (num <= 4.5) {
    return "#E1B955";
  } else {
    return "#C26B2F";
  }
};

export const extractTextFromQuill = (htmlContent) => {
  if (!htmlContent) return "";

  // Create a temporary DOM element to parse the HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");

  // Remove audio, video, and image elements
  const mediaElements = doc.querySelectorAll("audio, video, img, iframe");
  mediaElements.forEach((el) => el.remove());

  // textContent glues "<p>One</p><p>Two</p>" into "OneTwo", so the voice ran
  // paragraphs together without pausing. Mark each block's end with a newline
  // so text-to-speech can pause between them.
  doc.querySelectorAll("br").forEach((el) => el.replaceWith("\n"));
  doc
    .querySelectorAll("p, div, li, h1, h2, h3, h4, h5, h6, tr, blockquote")
    .forEach((el) => el.append("\n"));

  // Extract plain text content
  const plainText = doc.body.textContent || doc.body.innerText || "";

  // Clean up extra whitespace, keeping the line breaks
  return plainText
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("\n");
};

// Vocabulary has no column of its own on the accommodation form, so the
// read-aloud accommodations follow whatever is ticked for Reading.
const VOCABULARY_FOLLOWS_READING = [
  ACCOMMODATION_TYPE.READ_INSTRUCTIONS,
  ACCOMMODATION_TYPE.READ_QUESTIONS,
];

export const isAccommodationEnabled = (questionData, accommodationType) => {
  if (!questionData || !accommodationType) return false;
  const domain =
    questionData?.domain === QUESTION_SET_DOMAIN.VOCABULARY &&
    VOCABULARY_FOLLOWS_READING.includes(accommodationType)
      ? QUESTION_SET_DOMAIN.READING
      : questionData?.domain;
  return (
    questionData?.testSession?.accommodation?.[accommodationType]?.[domain] ||
    false
  );
};

const DOMAIN_LABELS = {
  listening: "Listening",
  reading: "Reading",
  writing: "Writing",
  speaking: "Speaking",
};

const TIME_EXTENSION_LABELS = {
  "5-minutes": "+ 5 minutes",
  "10-minutes": "+ 10 minutes",
  "20-minutes": "+ 20 minutes",
  infinite: "Timer Removed",
};

function getActiveDomainLabels(obj, isTimeExtension = false) {
  if (!obj || typeof obj !== "object") return [];
  return Object.entries(obj)
    .filter(([, v]) => (isTimeExtension ? v && v !== "none" : Boolean(v)))
    .map(([k]) => DOMAIN_LABELS[k] || k)
    .filter(Boolean);
}

function getTimeExtensionParts(obj) {
  if (!obj || typeof obj !== "object") return [];
  return Object.entries(obj)
    .filter(([, v]) => v && v !== "none")
    .map(
      ([domain, value]) =>
        `${DOMAIN_LABELS[domain] || domain}: ${
          TIME_EXTENSION_LABELS[value] || value
        }`
    )
    .filter(Boolean);
}

export function getAccommodationIconTitle(accommodation, key, baseTitle) {
  if (!accommodation || !baseTitle) return baseTitle || "";
  const obj = accommodation[key];
  const isTimeExtension = key === "time_extension";
  let parts = [];
  if (key === "read_questions" && obj?.reading) parts = ["Reading"];
  else if (key === "handwritten_answer" && obj?.writing) parts = ["Writing"];
  else if (isTimeExtension) parts = getTimeExtensionParts(obj);
  else parts = getActiveDomainLabels(obj, false);
  if (!parts.length) return baseTitle;
  return `${baseTitle}: ${parts.join(", ")}`;
}

// Students in grades 3-5 always write their answers on paper, so the handwritten
// response accommodation is forced on for them and cannot be unticked on the
// student record. It is only cleared when they are promoted out of the band.
export const HANDWRITTEN_ANSWER_MIN_GRADE = 3;
export const HANDWRITTEN_ANSWER_MAX_GRADE = 5;

export const isHandwrittenAnswerLocked = (grade) => {
  const value = Number(grade);
  return (
    Number.isFinite(value) &&
    value >= HANDWRITTEN_ANSWER_MIN_GRADE &&
    value <= HANDWRITTEN_ANSWER_MAX_GRADE
  );
};

export function getActiveAccommodationKeys(accommodation) {
  if (!accommodation || typeof accommodation !== "object") return [];
  const keys = [];
  if (accommodation.time_extension) {
    const vals = Object.values(accommodation.time_extension);
    if (vals.some((v) => v && v !== "none")) keys.push("time_extension");
  }
  if (accommodation.read_instructions) {
    if (Object.values(accommodation.read_instructions).some(Boolean))
      keys.push("read_instructions");
  }
  if (accommodation.read_questions?.reading) keys.push("read_questions");
  if (accommodation.handwritten_answer?.writing)
    keys.push("handwritten_answer");
  if (accommodation.assistance) {
    if (Object.values(accommodation.assistance).some(Boolean))
      keys.push("assistance");
  }
  if (accommodation.small_group) {
    if (Object.values(accommodation.small_group).some(Boolean))
      keys.push("small_group");
  }
  if (accommodation.scribe) {
    if (Object.values(accommodation.scribe).some(Boolean)) keys.push("scribe");
  }
  return keys;
}
export const countWordsInString = (value) => {
  if (!value) return 0;
  const str = String(value).trim();
  return str.split(/\s+/).filter(Boolean).length;
};
export const countWordsFromQuill = (value) => {
  if (!value) return 0;

  const text = String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!text) return 0;

  return text.split(" ").length;
};

export const sortDataStudentsFcInsights = (data, sortBy, sortOrder) => {
  const keys = {
    listening: "listeningScore",
    speaking: "speakingScore",
    reading: "readingScore",
    writing: "writingScore",
    firstName: "firstName",
    lastName: "lastName",
    grade: "grade",
    overAllScore: "overAllScore",
    studentStatus: "studentStatus",
    studentSupportLevel: "studentSupportLevel",
  };
  if (!data) return [];
  const tempData = [...data];

  const _sortedData = tempData.sort((a, b) => {
    if (sortOrder === "asc") {
      if (typeof a[keys[sortBy]] === "string") {
        return a[keys[sortBy]]
          .toLowerCase()
          .localeCompare(b[keys[sortBy]].toLowerCase());
      }
      return a[keys[sortBy]] - b[keys[sortBy]];
    } else {
      if (typeof a[keys[sortBy]] === "string") {
        return b[keys[sortBy]]
          .toLowerCase()
          .localeCompare(a[keys[sortBy]].toLowerCase());
      }
      return b[keys[sortBy]] - a[keys[sortBy]];
    }
  });
  return _sortedData;
};

export const getLanguageLabel = (language) => {
  if (!language) return "-";
  const opt = learningLanguagesOptions?.find((opt) => opt?.value === language);
  if (opt) return opt?.label;
  return capitalizeFirstLetter(language);
};

export const generateCsv = (data, fields) => {
  const csv = Papa.unparse({
    fields: fields,
    data: data,
    quotes: true, // Wrap every field in quotes
    quoteChar: '"',
    escapeChar: '"',
    delimiter: ",",
    header: true,
    newline: "\r\n",
    skipEmptyLines: false, // Skip empty lines
    columns: null, // Specify columns if you want to export only specific ones
  });

  return csv;
};

export const downloadCsv = (data, fieldsObj, fieldsArr, csvName) => {
  const leadsData = data?.map((item) => {
    const obj = {};
    fieldsArr?.forEach((field) => {
      obj[field] = item[fieldsObj[field]];
    });
    return obj;
  });
  const csv = generateCsv(leadsData, fieldsArr);
  // Leading BOM forces Excel to read this as UTF-8 text. Without it, a header
  // row starting with "ID" (as several of our CSV exports do) gets misread as
  // a SYLK file signature, and Excel throws a false "format and extension
  // don't match" warning before it will open the file.
  const blob = new Blob(["﻿" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = `${csvName}.csv`;
  link.click();
};
const twoDigitYear = (y) => String(y % 100).padStart(2, "0");

export function buildCalendarCycleOptions() {
  const startYear = new Date().getFullYear();
  const options = [];
  for (let y = startYear; y <= startYear + 5; y++) {
    for (let c = 1; c <= 3; c++) {
      const label = `SY${y} Cycle ${c}`;
      options.push({ label, value: label });
    }
  }
  return options;
}

export function buildAcademicCycleOptions() {
  const endingYear = new Date().getFullYear();
  const options = [];
  for (let y = endingYear; y <= endingYear + 5; y++) {
    const sy = `${twoDigitYear(y - 1)}${twoDigitYear(y)}`;
    for (let c = 1; c <= 4; c++) {
      const label = `SY${sy} Cycle ${c}`;
      options.push({ label, value: label });
    }
  }
  return options;
}
// Mirrors getRequiredCredits on the server. A summative task assesses all five
// domains and costs a full credit per student; the two half-tasks cost half.
// Used to quote the cost of a cycle before it is moved to conduct — the server
// is still the authority, this only keeps the charge from being a surprise.
export const getRequiredCredits = (task, studentCount) => {
  if (!studentCount) return 0;
  return task === "summative" ? studentCount : studentCount * 0.5;
};

// Accepts either a quick assessment or a bare date.
//
// `admissionDate` is only ever written when someone completes the admission, so
// it is null for every quick assessment nobody has enrolled yet — which used to
// render as literally "ADMS Invalid Date". `createdAt` always exists, and is
// what the other two call sites already fell back to.
//
// Returns null rather than a string when there is no date at all, so callers'
// `|| "--"` fallbacks actually get a chance to fire.
export const getAdmissionCycleName = (admission) => {
  const date =
    admission?.admissionDate || admission?.createdAt || admission || null;

  if (!date) return null;

  return `ADMS ${formatDate(date, "MMM YYYY")}`;
};

// Caption for one point on a student's history graph.
//
// Reporting merges by academic term, so a single point can be built from
// assessment-cycle sessions, quick-assessment sessions, or both. The scores are
// deliberately averaged together — this caption is the only thing that says
// where they came from.
export const getGraphPointLabel = (descriptor) => {
  // Anything tied to an academic term is labelled with that term: cycle work,
  // transferred quick assessments and manually created ones all merge into one
  // point, because that is how the reporting already aggregates them.
  //
  // The descriptor still carries hasCycle / hasQA, and the quick assessment's
  // sourceAssessmentCycle, so these can be told apart on the graph later
  // without touching the API — deliberately unused for now.
  const cycleName = descriptor?.cycle?.name;

  if (cycleName) return cycleName;

  // No term at all means an admission quick assessment for a new candidate.
  return getAdmissionCycleName(descriptor?.admission) || "--";
};

// Name of the term a report's Current Data actually describes. With report
// visibility gating on, that can be an older, released term rather than the one
// the student sat most recently — which is all `fallback` (getCycleName) can
// name. The API only sends `currentDataTerm` in that mode.
export const getCurrentDataLabel = (studentData, fallback) =>
  studentData?.currentDataTerm
    ? getGraphPointLabel(studentData.currentDataTerm)
    : fallback;

export const getAdmissionStatus = (status, admissionDate) => {
  if (status === studentStatus.ADMISSION_PENDING && !admissionDate) {
    return "pending";
  }
  if (status === studentStatus.ADMISSION_PENDING && admissionDate) {
    return "not-enrolled";
  }
  return "enrolled";
};

// routing is driven off the notification `flag`, never the copy — shared by
// the Notifications page and the Dashboard notifications card
export const getNotificationRedirectPath = (flag, payload, userRole) => {
  switch (flag) {
    // conduct / evaluate a quick assessment — assigned before any test
    // session exists, so the detail screen is what we can link to
    case "quick-assessment-conduct":
    case "quick-assessment-evaluate":
      return payload?.quickAssessment
        ? `/quick-assessment/${payload.quickAssessment}`
        : null;

    // quick assessment test session completed — evaluate that session
    case "quick-assessment":
      if (payload?.testSession)
        return `/quick-assessment-evaluate/${payload.testSession}`;
      if (payload?.quickAssessment)
        return `/quick-assessment/${payload.quickAssessment}`;
      return null;

    // conduct assessments for a cycle venue
    case "assessment-cycle-conduct":
      return payload?.assessmentCycle
        ? `/group-assessment/${payload.assessmentCycle}?tab=conduct${
            payload?.venueId ? `&venueId=${payload.venueId}` : ""
          }`
        : null;

    // standardization practice
    case "assessment-cycle-standardize":
      return payload?.assessmentCycle
        ? `/group-assessment/${payload.assessmentCycle}?tab=standardize${
            payload?.domain ? `&domain=${payload.domain}` : ""
          }`
        : null;

    // complete evaluations
    case "assessment-cycle-evaluate":
      return payload?.assessmentCycle
        ? `/group-assessment/${payload.assessmentCycle}?tab=evaluate${
            payload?.domain ? `&domain=${payload.domain}` : ""
          }`
        : null;

    // shared flag: evaluator gets it when a cycle test session completes
    // (payload carries the session), students get it with an empty payload
    // when their assessments are created
    case "assessment-cycle":
      if (payload?.testSession)
        return `/my-evaluation-evaluate/${payload.testSession}`;
      if (payload?.assessmentCycle)
        return `/group-assessment/${payload.assessmentCycle}?tab=evaluate`;
      return userRole === roles.STUDENT ? "/tests" : "/my-evaluations";

    // none, chat, user — nothing emits them, treat as non-clickable
    default:
      return null;
  }
};
